import type { ApolloClient } from '@apollo/client';
import type { HostedFields } from 'braintree-web';
import pick from 'lodash/pick';
import type { Dispatcher } from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import user from 'js/lib/user';

import { includesWithNarrowing } from 'bundles/common/utils/includesWithNarrowing';
import { paymentMethods } from 'bundles/payments-common/common/constants';
import type {
  BillingAddress,
  BillingInfo,
  CheckoutMutation,
  CreditCard,
  FormSettings,
  PaymentSubmission,
  SendEmailForRazorpayLiteToPlusUpgradeMutation,
  StripeClient,
  pollForProductOwnershipQuery,
} from 'bundles/payments-common/common/types';

const allowedPaymentMethods = Object.values(paymentMethods);
type AllowedPaymentMethod = (typeof paymentMethods)[keyof typeof paymentMethods];

const SERIALIZED_PROPS: Array<keyof PaymentFormStore$DehydratedState> = [
  'billingInfo',
  'paymentMethod',
  'settings',
  'submission',
  'randomNumber',
];

type PaymentFormStore$DehydratedState = {
  billingInfo: BillingInfo;
  paymentMethod?: string | null;
  settings: FormSettings;
  submission: PaymentSubmission;
  randomNumber: number;
};

class PaymentFormStore extends BaseStore {
  declare billingInfo: BillingInfo;

  declare paymentMethod?: AllowedPaymentMethod | null;

  declare settings: FormSettings;

  declare submission: PaymentSubmission;

  declare braintreeHostedFields: HostedFields | undefined;

  declare stripeClient?: StripeClient;

  declare checkoutMutation: CheckoutMutation | undefined;

  declare sendEmailForRazorpayLiteToPlusUpgradeMutation: SendEmailForRazorpayLiteToPlusUpgradeMutation | undefined;

  declare pollForProductOwnershipQuery: pollForProductOwnershipQuery | undefined;

  declare apolloClient: ApolloClient<unknown> | undefined;

  declare randomNumber: number;

  declare emitChange: () => void;

  static storeName = 'PaymentFormStore';

  static handlers = {
    SET_PAYMENT_METHOD: 'onSetPaymentMethod',
    SET_CHECKOUT_MUTATION: 'onSetCheckoutMutation',
    SET_RAZORPAY_UPGRADE_EMAIL_MUTATION: 'onSetSendEmailForRazorpayLiteToPlusUpgradeMutation',
    SET_POLL_FOR_PRODUCT_OWNERSHIP_QUERY: 'onSetPollForProductOwnershipQuery',
    SET_APOLLO_CLIENT: 'onSetApolloClient',
    SET_BRAINTREE_HOSTED_FIELDS: 'onSetBraintreeHostedFields',
    SET_STRIPE_CLIENT: 'onSetStripeClient',
    CLEAR_BRAINTREE_HOSTED_FIELDS: 'onClearBraintreeHostedFields',
    CLEAR_STRIPE_CLIENT: 'onClearStripeClient',
    UPDATE_BILLING_INFO: 'onUpdateBillingInfo',
    UPDATE_BILLING_ADDRESS: 'onUpdateBillingAddress',
    UPDATE_CREDIT_CARD: 'onUpdateCreditCard',
    UPDATE_PAYMENT_SUBMISSION: 'onUpdatePaymentSubmission',
    UPDATE_SETTINGS: 'onUpdateSettings',
    UPDATE_STORE: 'onUpdateStore',
  };

  onSetPaymentMethod(paymentMethod: string) {
    if (includesWithNarrowing(allowedPaymentMethods, paymentMethod) && paymentMethod !== this.paymentMethod) {
      this.setInitialBillingInfo();
      this.setInitialPaymentSubmission();
      this.paymentMethod = paymentMethod;
      this.emitChange();
    }
  }

  onSetCheckoutMutation(checkoutMutation: CheckoutMutation) {
    this.checkoutMutation = checkoutMutation;
    this.emitChange();
  }

  onSetSendEmailForRazorpayLiteToPlusUpgradeMutation(
    sendEmailForRazorpayLiteToPlusUpgradeMutation: SendEmailForRazorpayLiteToPlusUpgradeMutation
  ) {
    this.sendEmailForRazorpayLiteToPlusUpgradeMutation = sendEmailForRazorpayLiteToPlusUpgradeMutation;
    this.emitChange();
  }

  onSetPollForProductOwnershipQuery(pollForProductOwnershipQuery: pollForProductOwnershipQuery) {
    this.pollForProductOwnershipQuery = pollForProductOwnershipQuery;
    this.emitChange();
  }

  onSetApolloClient(apolloClient: ApolloClient<unknown>) {
    this.apolloClient = apolloClient;
    this.emitChange();
  }

  onSetBraintreeHostedFields(braintreeHostedFields: HostedFields) {
    this.braintreeHostedFields = braintreeHostedFields;
    this.emitChange();
  }

  onSetStripeClient(stripeClient: StripeClient) {
    this.stripeClient = stripeClient;
    this.emitChange();
  }

  onClearBraintreeHostedFields() {
    delete this.braintreeHostedFields;
    this.emitChange();
  }

  onClearStripeClient() {
    delete this.stripeClient;
    this.emitChange();
  }

  onUpdateBillingInfo(fields: BillingInfo) {
    this.billingInfo = Object.assign({}, this.billingInfo, fields);
    this.emitChange();
  }

  onUpdateBillingAddress(fields: BillingAddress) {
    this.billingInfo.billingAddress = {
      ...this.billingInfo.billingAddress,
      ...fields,
    };
    this.emitChange();
  }

  onUpdateCreditCard(fields: CreditCard) {
    this.billingInfo.creditCard = Object.assign({}, this.billingInfo.creditCard, fields);
    this.emitChange();
  }

  onUpdatePaymentSubmission(fields: PaymentSubmission) {
    this.submission = Object.assign({}, this.submission, fields);
    this.emitChange();
  }

  onUpdateSettings(settings: FormSettings) {
    this.settings = Object.assign({}, this.settings, settings);
    this.emitChange();
  }

  onUpdateStore(state: PaymentFormStore$DehydratedState) {
    this.billingInfo = Object.assign({}, this.billingInfo, state.billingInfo);
    this.paymentMethod = includesWithNarrowing(allowedPaymentMethods, state.paymentMethod)
      ? state.paymentMethod
      : this.paymentMethod;
    this.settings = Object.assign({}, this.settings, state.settings);
    this.emitChange();
  }

  dehydrate(): PaymentFormStore$DehydratedState {
    return pick(this, ...SERIALIZED_PROPS);
  }

  rehydrate(state: PaymentFormStore$DehydratedState) {
    Object.assign(this, pick(state, ...SERIALIZED_PROPS));
  }

  constructor(dispatcher: Dispatcher) {
    super(dispatcher);
    this.setInitialBillingInfo();
    this.setInitialPaymentSubmission();
    this.paymentMethod = null;
    this.settings = {
      shouldStoreCreditCard: false,
      isSandboxEnabled: false,
      isCreateFakeOrgEnabled: false,
      isMockRequest: false,
      isMockPaymentProcessorCheckoutEnabled: false,
    };
    this.randomNumber = Math.random();
  }

  setInitialPaymentSubmission(): void {
    this.submission = {
      isSubmitting: false,
      errorCode: null,
      errorDetails: null,
      response: null,
    };
  }

  setInitialBillingInfo(): void {
    this.billingInfo = {
      creditCard: {
        cardholderName: user.get().full_name,
      },
    };
  }

  getPaymentMethod(): AllowedPaymentMethod | null | undefined {
    return this.paymentMethod;
  }

  getBraintreeHostedFields(): HostedFields | undefined {
    // Protect against accessing a "torn down" HostedFields (i.e. one on which `.teardown()` has been called).
    try {
      // We don't care about the value, we just want to see if calling it will throw an error.
      this.braintreeHostedFields?.getState();
    } catch {
      // We could check if (e.message === 'getState cannot be called after teardown.') here, but that seems a bit
      // brittle. And if getState is not working for any other reason, it's still probably better to return undefined.
      return undefined;
    }
    return this.braintreeHostedFields;
  }

  getStripeClient(): StripeClient | undefined {
    return this.stripeClient;
  }

  getCheckoutMutation(): CheckoutMutation | undefined {
    return this.checkoutMutation;
  }

  getSendEmailForRazorpayLiteToPlusUpgradeMutation(): SendEmailForRazorpayLiteToPlusUpgradeMutation | undefined {
    return this.sendEmailForRazorpayLiteToPlusUpgradeMutation;
  }

  getPollForProductOwnershipQuery(): pollForProductOwnershipQuery | undefined {
    return this.pollForProductOwnershipQuery;
  }

  getApolloClient(): ApolloClient<unknown> | undefined {
    return this.apolloClient;
  }

  getBillingInfo(): BillingInfo {
    return this.billingInfo;
  }

  getSettings(): FormSettings {
    return this.settings;
  }

  getPaymentSubmission(): PaymentSubmission {
    return this.submission;
  }

  getRandomNumber(): number {
    return this.randomNumber;
  }
}

export default PaymentFormStore;
